import { useCallback, useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash-es';

import { useDeferredAsync } from './useAsync';

import { validateGoogleSlidesUrl } from 'slices/slides';

// Matches: https://docs.google.com/presentation/d/FILE_ID/edit, /pub, /embed, /preview, or no suffix
export const GOOGLE_SLIDES_URL_REGEX = /^https?:\/\/docs\.google\.com\/presentation\/d\/([a-zA-Z0-9_-]+)(?:\/|$|\?)/;

export const isGoogleSlidesUrl = (url: string): boolean => {
  return GOOGLE_SLIDES_URL_REGEX.test(url);
};

interface UseGoogleSlidesUrlValidationParams {
  topicId: string;
  url: string;
  hasYupError: boolean;
}

interface UseGoogleSlidesUrlValidationResult {
  validating: boolean;
  validated: boolean;
  validationFailed: boolean;
  formatError: boolean;
  reset: () => void;
}

export const useGoogleSlidesUrlValidation = ({
  topicId,
  url,
  hasYupError,
}: UseGoogleSlidesUrlValidationParams): UseGoogleSlidesUrlValidationResult => {
  const [formatError, setFormatError] = useState(false);

  const {
    run: validateUrl,
    pending: validating,
    succeeded: validated,
    failed: validationFailed,
    resetState,
  } = useDeferredAsync(validateGoogleSlidesUrl);

  const validateUrlDebouncedRef = useRef<ReturnType<typeof debounce>>();

  useEffect(() => {
    validateUrlDebouncedRef.current?.cancel();
    validateUrlDebouncedRef.current = debounce(
      (urlToValidate: string) => validateUrl({ topicId, url: urlToValidate }),
      300,
    );

    return () => {
      validateUrlDebouncedRef.current?.cancel();
    };
  }, [validateUrl, topicId]);

  useEffect(() => {
    // Reset validation state when URL changes
    resetState();
    setFormatError(false);

    // Only validate if the URL passes basic Yup validation (is a valid URL format)
    if (!hasYupError && url) {
      // First check: Does it match the Google Slides URL pattern?
      if (!isGoogleSlidesUrl(url)) {
        setFormatError(true);
        return; // Don't call API if the format is wrong
      }

      // Second check: Call API to validate access
      validateUrlDebouncedRef.current?.(url);
    }

    return () => {
      validateUrlDebouncedRef.current?.cancel();
    };
  }, [url, hasYupError, resetState]);

  const reset = useCallback(() => {
    resetState();
    setFormatError(false);
  }, [resetState]);

  return {
    validating,
    validated,
    validationFailed,
    formatError,
    reset,
  };
};

export default useGoogleSlidesUrlValidation;
